import { Kbd } from './primitives.tsx'
import styles from './SearchField.module.css'

/**
 * The one search input (ui-components.md lists it; three hand-rolled copies existed instead).
 *
 * `scope` is what makes the `/` shortcut work. It used to grab
 * `document.querySelector('input[type="search"]')`, which returns the first match in the DOM —
 * always the top bar, because AppShell renders above the outlet. So on /table the shortcut focused
 * the global field while the one that filters the table sat right there, and below 700 px it
 * focused an element that CSS had hidden. Views mark their own field `view`; the top bar marks
 * itself `global` and is only used when the view has none.
 */
export function SearchField({
  value,
  onChange,
  placeholder,
  label,
  scope,
  onSubmit,
  className,
  showHint = false,
}: {
  value: string
  onChange: (next: string) => void
  placeholder: string
  label: string
  scope: 'view' | 'global'
  onSubmit?: (value: string) => void
  /**
   * Layout and responsive rules stay with the caller — only the look of the field lives here.
   * Explicit `| undefined` because exactOptionalPropertyTypes is on and CSS Module lookups are
   * typed as possibly undefined.
   */
  className?: string | undefined
  showHint?: boolean
}): React.JSX.Element {
  return (
    <div className={className === undefined ? styles.field : `${styles.field} ${className}`}>
      <input
        className={styles.input}
        type="search"
        data-hotkey-search={scope}
        placeholder={placeholder}
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && onSubmit) {
            e.preventDefault()
            onSubmit(value)
          }
          if (e.key === 'Escape') e.currentTarget.blur()
        }}
      />
      {showHint && value === '' && (
        <span className={styles.hint}>
          <Kbd>/</Kbd>
        </span>
      )}
    </div>
  )
}
